import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useBrsCreateStyles } from '../BrsCreateStyles';
import { checkOriginalIsciExists, getAssetData } from '../../../helpers';

export const OriginalIsciRow = ({
  index,
  data,
  onChange,
  disabled,
  isciAssetExists,
  isPrePopulated,
  includeSD,
  brandChoices,
  onDurationChange,
}) => {
  const classes = useBrsCreateStyles();
  const [validating, setValidating] = useState(false);
  const [isValid, setIsValid] = useState(null);

  // The blur handler awaits two network calls before writing back via
  // onChange. By the time we resume, the parent has re-rendered (the H-append
  // we did synchronously already triggered one render) and `data` / `onChange`
  // captured in the useCallback closure point at a stale snapshot. Calling
  // that stale onChange walks the parent chain (handleOriginalIsciChange →
  // updateIsciAsset → handleSpotChange), each of which reads `spot` / `spots`
  // from its own stale closure, and the resulting setSpots clobbers the
  // intermediate state — leaving the title/advertiser fields visually empty
  // until a second blur. BrsCreateStrata avoids this by using React Final
  // Form's form.change() which writes directly to a stable form-state ref.
  // Mirror that here with refs that always point at the latest props so the
  // post-await write goes through the freshest parent chain.
  const dataRef = useRef(data);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    dataRef.current = data;
    onChangeRef.current = onChange;
  });

  const handleFieldChange = useCallback(
    (field, value) => {
      onChange({ ...data, [field]: value });
    },
    [data, onChange],
  );

  const handleIsciBlur = useCallback(
    async (e) => {
      let value = e.target.value;
      if (!value) {
        setIsValid(null);
        return;
      }
      if (!value.endsWith('H')) {
        value = value + 'H';
        handleFieldChange('isci', value);
      }

      setValidating(true);
      const isci1 = value;
      const isci2 = value.endsWith('H') ? value.slice(0, -1) : value;
      const args = includeSD ? [isci1, isci2] : [isci1];

      const exists = await checkOriginalIsciExists(...args);
      setIsValid(exists);

      if (exists) {
        const assetData = await getAssetData(value);
        if (assetData) {
          // Read latest data + onChange via refs to dodge the stale closure
          // captured at blur time (see comment above the refs).
          const currentData = dataRef.current;
          const updates = { ...currentData, isci: value };
          if (!currentData.advertiser_name && assetData.brand) {
            updates.advertiser_name = assetData.brand;
          }
          if (!currentData.title && assetData.title) {
            updates.title = assetData.title;
          }
          // Write title/advertiser BEFORE bumping the duration. In React 17
          // each post-await setState commits synchronously but the
          // useEffect that syncs IsciEntryCard's totalDuration into
          // spot.isciasset.duration is deferred. If setDurations runs
          // first, that effect is scheduled with a closure that captured
          // `spot` BEFORE these title/advertiser writes, and when it later
          // calls onChange to push the new duration it clobbers them via
          // handleSpotChange's stale `spots` closure (the symptom: first
          // blur leaves the fields empty, second blur succeeds because
          // the duration already matches and the effect short-circuits).
          // Reversing the order makes the duration effect capture the
          // post-write spot so its later onChange preserves the updates.
          onChangeRef.current(updates);
          if (assetData.duration) {
            onDurationChange(index, assetData.duration);
          }
        }
      }
      setValidating(false);
    },
    [includeSD, index, onDurationChange, handleFieldChange],
  );

  const fieldsDisabled = disabled || (isciAssetExists && isValid !== false);

  // BRS-FORM-P1 Phase 1 / SOW dependency: "The Brand field on traffic
  // updates will be editable." So even when the rest of the row is locked
  // (pre-populated traffic update), the Brand <select> must remain usable.
  const brandDisabled = isPrePopulated ? false : fieldsDisabled;

  // Pre-populated rows come back from the backend with brand_name/brand_code
  // but no brand-id (original_isci persists the name/code pair, not the
  // brand FK). Resolve the selected option at render time by matching the
  // brand_code against the loaded brand catalog so the prior delivery's
  // brand shows up in the select instead of the empty "Select brand..."
  // placeholder.
  const selectedBrandId =
    data.brand ||
    (data.brand_code &&
      (brandChoices || []).find((b) => b.code === data.brand_code)?.id) ||
    '';

  return (
    <div className={classes.origIsciRow}>
      <div className={classes.origIsciFieldFixed}>
        <label className={classes.formLabel}>Original ISCI {index + 1}</label>
        <input
          className={classes.formInput}
          value={data.isci || ''}
          onChange={(e) => handleFieldChange('isci', e.target.value)}
          onBlur={handleIsciBlur}
          placeholder="Enter ISCI..."
          disabled={disabled}
        />
        {validating && <div className={classes.helperText}>Checking ISCI...</div>}
        {isValid === false && <div className={classes.fieldError}>Original ISCI not found</div>}
      </div>
      <div className={classes.origIsciField}>
        <label className={classes.formLabel}>Title</label>
        <input
          className={`${classes.formInput} ${fieldsDisabled ? classes.formInputReadonly : ''}`}
          value={data.title || ''}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          disabled={fieldsDisabled}
        />
      </div>
      <div className={classes.origIsciField}>
        <label className={classes.formLabel}>Advertiser Name</label>
        <input
          className={`${classes.formInput} ${fieldsDisabled ? classes.formInputReadonly : ''}`}
          value={data.advertiser_name || ''}
          onChange={(e) => handleFieldChange('advertiser_name', e.target.value)}
          disabled={fieldsDisabled}
        />
      </div>
      <div className={classes.origIsciField}>
        <label className={classes.formLabel}>Brand *</label>
        <select
          className={classes.formSelect}
          value={selectedBrandId}
          onChange={(e) => {
            const brandId = e.target.value;
            const brand = brandChoices.find((b) => b.id === brandId);
            if (brand) {
              onChange({ ...data, brand: brandId, brand_name: brand.name, brand_code: brand.code });
            } else {
              handleFieldChange('brand', brandId);
            }
          }}
          disabled={brandDisabled}
        >
          <option value="">Select brand...</option>
          {brandChoices.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>
      <div className={classes.origIsciFieldBrandCode}>
        <label className={classes.formLabel}>Brand Code</label>
        <input
          className={`${classes.formInput} ${classes.formInputReadonly}`}
          value={data.brand_code || ''}
          readOnly
        />
      </div>
    </div>
  );
};
